import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { PhoneAuthForm } from './PhoneAuthForm';
import { useUiStore } from '@app/store/ui.store';
import styles from './AuthModal.module.css';

/**
 * AuthModal — глобальная модалка входа.
 *
 * Управляется через ui.store:
 *   const openAuthModal = useUiStore(s => s.openAuthModal)
 *   <button onClick={() => openAuthModal()}>Войти</button>
 *
 * Рендерится через React Portal в document.body.
 * Закрывается по Escape, клику на overlay, кнопке ✕ или автоматически
 * после успешного входа (внутри PhoneAuthForm).
 *
 * Полноценный экран /app/auth остаётся как fallback для direct URL.
 */
export function AuthModal() {
  const isOpen = useUiStore((s) => s.authModalOpen);
  const options = useUiStore((s) => s.authModalOptions);
  const close = useUiStore((s) => s.closeAuthModal);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    // Блокируем скролл body пока модалка открыта
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, close]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className={styles.modalOverlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
      role="presentation"
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label={options.title ?? 'Вход в Aura'}
      >
        <header className={styles.modalHeader}>
          <div className={styles.modalTitles}>
            <h2 className={styles.modalTitle}>{options.title ?? 'Вход в Aura'}</h2>
            <p className={styles.modalSubtitle}>
              Введите номер телефона — отправим SMS с кодом
            </p>
          </div>
          <button
            type="button"
            className={styles.modalClose}
            onClick={close}
            aria-label="Закрыть"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6l12 12M6 18L18 6" />
            </svg>
          </button>
        </header>

        <PhoneAuthForm onSuccess={close} showBackButton />

        <p className={styles.legal}>
          Продолжая, вы соглашаетесь с{' '}
          <a href="/user_agreement" target="_blank" rel="noreferrer">
            пользовательским соглашением
          </a>{' '}
          и{' '}
          <a href="/privacy_policy" target="_blank" rel="noreferrer">
            политикой конфиденциальности
          </a>
          .
        </p>
      </div>
    </div>,
    document.body
  );
}
